import { environment } from 'src/environments/environment';

export interface TemplateOption {
  value: string;
  displayName: string;
}

export const TEMPLATE_OPTIONS_MAP: Record<string, string> = {
  civix: 'CIVIX',
  cipher: 'Cipher',
};

const DEFAULT_TEMPLATE_KEY = 'civix';

function getDefaultTemplateDisplayName(): string {
  const envServerName = String(environment?.serverName || '').trim();
  if (!envServerName) {
    return TEMPLATE_OPTIONS_MAP[DEFAULT_TEMPLATE_KEY];
  }

  const normalizedEnvName = envServerName.toLowerCase();
  const fromKey = TEMPLATE_OPTIONS_MAP[normalizedEnvName];
  if (fromKey) {
    return fromKey;
  }

  const fromDisplayName = Object.values(TEMPLATE_OPTIONS_MAP).find(
    (displayName) => displayName.toLowerCase() === normalizedEnvName
  );
  if (fromDisplayName) {
    return fromDisplayName;
  }

  return envServerName;
}

export function getTemplateDisplayName(templateValue?: string): string {
  if (!templateValue) {
    return getDefaultTemplateDisplayName();
  }
  return TEMPLATE_OPTIONS_MAP[templateValue] || getDefaultTemplateDisplayName();
}

export function getOrganizationTemplateDisplayName(organization?: any): string {
  const templateValue = organization?.settings?.template || organization?.template?.value || organization?.template;
  return getTemplateDisplayName(templateValue);
}

export function getStoredOrganizationTemplateDisplayName(): string {
  try {
    const rawOrg = localStorage.getItem('org');
    if (!rawOrg) {
      return getTemplateDisplayName();
    }
    const org = JSON.parse(rawOrg);
    return getOrganizationTemplateDisplayName(org);
  } catch {
    return getTemplateDisplayName();
  }
}

export function getOrganizationTemplateKey(organization?: any): string {
  const raw = organization?.settings?.template || organization?.template?.value || organization?.template;
  const key = String(raw || '').trim().toLowerCase();
  if (!key) {
    const envName = String(environment?.serverName || '').trim().toLowerCase();
    if (TEMPLATE_OPTIONS_MAP[envName]) {
      return envName;
    }
    return DEFAULT_TEMPLATE_KEY;
  }
  return key;
}

export function getStoredOrganizationTemplateKey(): string {
  try {
    const rawOrg = localStorage.getItem('org');
    if (!rawOrg) {
      return getOrganizationTemplateKey();
    }
    return getOrganizationTemplateKey(JSON.parse(rawOrg));
  } catch {
    return getOrganizationTemplateKey();
  }
}

export function resolveTemplateImageSrc(
  fileName: string,
  fallbackPath: string,
  organization?: any
): Promise<string> {
  return resolveTemplateAssetSrc(fileName, fallbackPath, organization);
}

/**
 * True when the response is a real image asset (not SPA index.html fallback for unknown paths).
 */
function responseLooksLikeImageAsset(response: Response): boolean {
  const ct = (response.headers.get('content-type') || '').toLowerCase();
  if (ct.includes('text/html')) {
    return false;
  }
  return ct.startsWith('image/');
}

export function resolveTemplateAssetSrc(
  fileName: string,
  fallbackPath: string,
  organization?: any
): Promise<string> {
  const templateKey = getOrganizationTemplateKey(organization);
  const candidatePath = `assets/images/template/${templateKey}/${fileName}`;

  if (typeof window === 'undefined') {
    return Promise.resolve(fallbackPath);
  }

  // Many hosts return 200 + index.html for missing static paths (SPA fallback). HEAD then looks "ok"
  // even though the file does not exist. Reject non-image Content-Type and verify with a small GET
  // when HEAD omits or misreports type.
  return fetch(candidatePath, { method: 'HEAD', cache: 'no-cache' })
    .then((response) => {
      if (!response.ok) {
        return fallbackPath;
      }
      const ctHead = (response.headers.get('content-type') || '').toLowerCase();
      if (ctHead.includes('text/html')) {
        return fallbackPath;
      }
      if (responseLooksLikeImageAsset(response)) {
        return candidatePath;
      }
      // HEAD ok but missing/ambiguous Content-Type — confirm with GET (logos are small).
      return fetch(candidatePath, { method: 'GET', cache: 'no-cache' })
        .then((getResponse) => {
          if (!getResponse.ok) {
            return fallbackPath;
          }
          if (responseLooksLikeImageAsset(getResponse)) {
            return candidatePath;
          }
          return getResponse.blob().then((blob) =>
            blob.type.startsWith('image/') && blob.size > 0 ? candidatePath : fallbackPath
          );
        });
    })
    .catch(() => fallbackPath);
}
